/**
 * 기사 홈 — 로그인 직후. 본인 정보는 입력 0으로 자동 표시.
 * 메뉴: 입찰(큰 버튼) · 묶음별 아이콘 칸(내 배차 · 내 내역보기 · 명세서 · 공지 · 서류신청(외부) · 운행관리(외부) · 차량 서류 · 예비기사 분배(차주) · 세금계산서).
 *
 * 10-02 way: 운행관리시스템(기사관리 앱 `dirverapp01` — 운행일지·주유·정비)은 구 버스인 기사 화면에서 넘어가던 메뉴라 다시 붙인다.
 * 그 앱은 로그인이 없고 `?driverId=` 가 곧 신원이다 — 기사ID 는 버스인 `driver_id` 와 같은 번호(4215 김종화 등 실측 일치).
 *
 * 2026-09-27 way: 계산서 신청은 아직 세금계산서 앱(dytaxdriver)이 정본이라 두 버튼을 하나로 합쳐 그리로 보낸다.
 * 서류는 서류신청 서비스(docuservice)에서 본다. 앱 안 화면(/invoices·/orders·/docs)은 지우지 않았다 —
 * 09-29: 세금계산서 타일은 **기사 앱 신청 스위치를 따라간다**(`getDriverMe.orderOpen`) — 켜지면 앱 안 두 타일(/invoices·/orders),
 * 꺼져 있으면 세금계산서 앱 한 타일. 여는 날 배포가 필요 없다. 서류신청은 스위치와 무관하게 docuservice 로.
 *
 * 자동 연동(09-27 way): 누르면 서버가 2분짜리 1회용 입장권을 붙인 주소를 준다(`issueDriverHandoff`) —
 * 서류신청은 로그인된 채로 열리고, 세금계산서 신청은 신청자 칸이 채워진다. 입장권을 못 받으면 그냥 첫 화면으로.
 *
 * 디자인(10-08 way 「아이콘 버튼을 세련되게 배열 · 더 전문적으로 · 폰 글자를 크게 키운 분이 많아 깨진다」):
 * 낮은 브랜드색 머리(이름·소속·차량 알약) + 입찰 큰 버튼 + **세 묶음(운행·차량 / 정산 / 서류·소식) · 칸마다 흰 카드**
 * (10-08 밤 way 「메뉴 간 구분이 없어 단조롭다 · 간격 밸런스」 — 한 카드 격자를 묶음별 카드로 바꿨다).
 * 폰 글자 배율(textScale.ts)이 크면 칸 수를 줄인다 — 1.25배↑ 2열 · 1.6배↑ 한 줄 목록. 글자 17px·터치 56px 하한은 그대로.
 */

const TAX_APP_URL = 'https://dytaxdirver.web.app/admin.html';
const DOCU_APP_URL = 'https://docuservice.web.app/';
const TRIP_APP_URL = 'https://drivermanager01-6e819.web.app/';

/**
 * 밖으로 나가는 주소에 「기사앱에서 왔다」 표시(`from=busin-driver`)를 붙인다(10-02 way 「돌아오는 버튼」).
 * 받는 앱 셋(세금계산서·서류신청·운행관리)이 이 표시를 보고 「← 기사앱으로」 버튼을 띄운다 — 설치형 앱(아이폰)엔 뒤로 가기가 없다.
 * 쿼리·해시(입장권)는 그대로 두고 해시 앞에 붙인다.
 */
function withReturnMark(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('from', 'busin-driver');
    return u.toString();
  } catch {
    return url;
  }
}

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../lib/driverAuth';
import { driverApi } from '../lib/driverApi';
import { startDriverPrefetch } from '../lib/driverCache';
import { Icon, type IconName } from './icons';
import { useTextScale } from './textScale';

type MenuItem = {
  key: string;
  icon: IconName;
  tint: keyof typeof TINTS;
  label: string;
  busy?: boolean;
  /** 이름이 길어 격자 한 칸에 안 들어가면 한 줄 전체(목록형). */
  wide?: boolean;
  external?: boolean;
  onClick: () => void;
};

export default function Home() {
  const { driver, allocs, isOwner, orderOpen, logout } = useDriverAuth();
  const navigate = useNavigate();
  const [opening, setOpening] = useState<'docuservice' | 'dytax' | null>(null);
  const text = useTextScale();

  // 메뉴 목록을 미리 받아 두고 함수들을 깨워 둔다 — 메뉴를 눌렀을 때 「불러오는 중」 없이 바로 뜨게(lib/driverCache.ts).
  useEffect(() => {
    startDriverPrefetch();
  }, []);

  // 그 앱에서 뒤로 가기로 돌아오면(bfcache) 「여는 중…」이 남아 있다 — 풀어 준다.
  useEffect(() => {
    const reset = () => setOpening(null);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  async function openLinked(target: 'docuservice' | 'dytax', fallback: string) {
    if (opening) return;
    setOpening(target);
    let url = fallback;
    try {
      url = (await driverApi.issueHandoff({ target })).url || fallback;
    } catch {
      /* 입장권이 안 나와도 그 앱 첫 화면으로는 보낸다 */
    }
    window.location.assign(withReturnMark(url));
  }

  const grpNames = [...new Set(allocs.map((a) => a.grp_name).filter(Boolean))].join(', ');
  const carNos = [...new Set(allocs.map((a) => a.car_no).filter(Boolean))].join(', ');

  const go = (key: string, icon: IconName, tint: keyof typeof TINTS, label: string, path: string): MenuItem => ({
    key,
    icon,
    tint,
    label,
    onClick: () => navigate(path),
  });

  // 메뉴를 하는 일별 세 묶음으로(10-08 way 「메뉴 간 구분이 없어 단조롭다 · 간격 밸런스」).
  const sections: { key: string; title: string; items: MenuItem[] }[] = [
    {
      key: 'drive',
      title: '운행·차량',
      items: [
        // 10-21 way: 「내 배차 신청」은 입찰 결과라 입찰 버튼과 겹쳤다 → 배정된 운행을 달별로 보는 「내 배차」(신청 결과는 입찰 화면 탭).
        go('my-dispts', 'calendar', 'sky', '내 배차', '/my-dispts'),
        ...(driver?.driver_id != null
          ? [
              {
                key: 'trip',
                icon: 'gauge' as const,
                tint: 'emerald' as const,
                label: '운행관리\u200b시스템',
                external: true,
                onClick: () => window.location.assign(withReturnMark(`${TRIP_APP_URL}?driverId=${encodeURIComponent(String(driver.driver_id))}`)),
              },
            ]
          : []),
        // 09-29 way — 레거시 기사 사이트에서 보던 차량 서류. 앱 안에서 바로 연다(지금 편성 차량만 · 서버가 거른다).
        go('car-docs', 'carDoc', 'sky', '차량등록증·​보험증', '/car-docs'),
      ],
    },
    {
      key: 'pay',
      title: '정산',
      items: [
        go('salary', 'wallet', 'emerald', '내 명세서', '/salary'),
        ...(orderOpen
          ? [
              go('invoices', 'receipt', 'rose', '세금계산서 신청', '/invoices'),
              go('orders', 'card', 'violet', '페이앱·카드·​현금영수증', '/orders'),
              // 10-12 기사 「예전처럼 내가 발행한 내역 보기 — 돋보기 그림 『내 내역보기』가 안 보인다」:
              // 내역은 위 두 칸 안에 있지만 이름이 「신청」이라 못 찾는다. 옛 이름·그림 그대로 한 줄을 둔다(계산서 내역으로 열고 위 탭으로 넘긴다).
              { ...go('history', 'search', 'sky', '내 내역보기 (계산서·페이앱·현금영수증)', '/invoices'), wide: true },
            ]
          : [
              {
                key: 'dytax',
                icon: 'receipt' as const,
                tint: 'rose' as const,
                label: '세금계산서·페이앱·카드·현금영수증',
                wide: true,
                busy: opening === 'dytax',
                external: true,
                onClick: () => void openLinked('dytax', TAX_APP_URL),
              },
            ]),
        // 차주만(휴대폰 번호가 차주 번호) — 예비기사에게 나눠 줄 금액. 차인지급액 조정(회계)에 들어간다.
        ...(isOwner ? [go('split', 'users', 'amber', '예비기사 분배', '/reserve-split')] : []),
      ],
    },
    {
      key: 'info',
      title: '서류·소식',
      items: [
        {
          key: 'docu',
          icon: 'file',
          tint: 'violet',
          label: '서류신청',
          busy: opening === 'docuservice',
          external: true,
          onClick: () => void openLinked('docuservice', DOCU_APP_URL),
        },
        go('notices', 'megaphone', 'amber', '공지사항', '/notices'),
      ],
    },
  ];

  return (
    // 한 화면에 다 보이게(10-08 way 「스크롤해서 내려야 하는 불편」) — 화면 높이를 세로 flex 로 채우고
    // 메뉴 칸이 남은 높이를 나눠 가진다(작은 폰은 줄고 큰 폰은 커진다). 글자를 키운 폰은 촘촘한 목록 카드.
    <main className="flex min-h-[100dvh] flex-col">
      {/* 머리는 최소로(10-08 way 「상단을 최대한 줄여도 된다 · 아래 메뉴가 더 중요」) — 이름 한 줄 + 소속·차량 알약. */}
      <header className="driver-hero px-4 pb-10 pt-[calc(var(--sat)+0.5rem)] text-white">
        <div className="relative flex items-center gap-3">
          <img src="/driver-icon-192.png" alt="" className="h-11 w-11 shrink-0 rounded-xl ring-1 ring-white/30" />
          <h1 className="min-w-0 flex-1 break-keep text-xl font-bold leading-tight">{driver?.driver_name ?? '기사'} 기사님</h1>
          <button
            className="flex min-h-touch shrink-0 items-center gap-1.5 rounded-full px-3 text-base text-white/85 transition active:bg-white/15"
            onClick={() => void logout()}
            aria-label="로그아웃"
          >
            <Icon name="logout" className="h-5 w-5" />
            {text === '' && '로그아웃'}
          </button>
        </div>
        <div className="relative mt-2.5 flex flex-wrap gap-2">
          <Chip icon="building" value={grpNames || '소속 없음'} />
          <Chip icon="bus" value={carNos || '차량 없음'} />
        </div>
      </header>

      {/* 로그인 화면과 같은 유선형 판(10-08 way 「로그인의 유선형 디자인을 메인에도」) — 보라 머리 위로 둥근 판이 올라온다. relative z-10 = 머리(.driver-hero)가 relative 라 없으면 판 윗부분이 머리 뒤로 숨는다. */}
      {/* 간격 — 바깥 16 · 묶음 사이 16 · 제목↔칸 8 · 칸 사이 10. */}
      <div className="relative z-10 -mt-7 flex flex-1 flex-col rounded-t-[28px] bg-canvas px-4 pb-[calc(var(--sab)+1rem)] pt-4">

        <button
          onClick={() => navigate('/bids')}
          className="flex min-h-[4.25rem] w-full items-center gap-3.5 rounded-2xl bg-brand-600 px-4 py-2.5 text-left text-white shadow-lg shadow-brand-600/25 transition active:scale-[0.99] active:bg-brand-700"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
            <Icon name="bus" className="h-7 w-7 text-leaf" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block break-keep text-xl font-bold leading-snug">입찰 배차 보기</span>
            {/* 글자를 키운 폰에선 설명이 두 줄로 늘어 메뉴를 밀어낸다 — 제목만. */}
            {text === '' && <span className="block break-keep text-base leading-snug text-white/80">지금 신청할 수 있는 배차</span>}
          </span>
          <Icon name="chevronRight" className="h-6 w-6 shrink-0 text-white/80" />
        </button>

        <div className="mt-4 flex flex-1 flex-col gap-4">
          {sections.map((s) => (
            <MenuSection key={s.key} title={s.title} items={s.items} text={text} />
          ))}
        </div>
      </div>
    </main>
  );
}

/** 머리 알약 — 소속·차량. 값이 길면(여러 대) 줄을 바꾼다. */
function Chip({ icon, value }: { icon: IconName; value: string }) {
  return (
    <span className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-base font-semibold ring-1 ring-white/20">
      <Icon name={icon} className="h-4 w-4 shrink-0 text-white/80" />
      <span className="min-w-0 break-all leading-snug">{value}</span>
    </span>
  );
}

/** 아이콘 바탕색 — Tailwind 가 클래스를 찾을 수 있게 통째 문자열로 둔다(조립하면 빌드에서 빠진다). */
const TINTS = {
  sky: 'bg-sky-50 text-sky-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  // 보라 계열은 회사 색(brand)으로 — violet 은 로고 보라와 겹쳐 «비슷한데 다른 보라»가 된다.
  violet: 'bg-brand-50 text-brand-600',
  rose: 'bg-rose-50 text-rose-600',
} as const;

/**
 * 묶음 하나 — 제목(색 막대 + 이름) 아래 칸들.
 * 보통 글자: 흰 칸 격자(2·4개 = 2열 · 3개 = 3열). 묶음이 격자 줄 수만큼 남은 높이를 나눠 가져 한 화면에 들어간다.
 * 글자를 키운 폰(l·xl)·격자 칸이 하나뿐인 묶음: 흰 카드 하나에 한 줄씩 — 큰 글자에서도 짧게.
 * 이름이 긴 칸(wide)은 격자 아래 한 줄 카드.
 */
function MenuSection({ title, items, text }: { title: string; items: MenuItem[]; text: '' | 'l' | 'xl' }) {
  const cells = items.filter((m) => !m.wide);
  const list = text !== '' || cells.length === 1;
  const n = cells.length;
  const cols = n === 3 ? 3 : 2;
  const gridRows = list ? 0 : Math.ceil(n / cols);
  const rows = list ? items : items.filter((m) => m.wide);
  return (
    <section className="flex flex-col" style={gridRows ? { flexGrow: gridRows, flexBasis: 0 } : undefined}>
      <h2 className="mb-2 flex items-center gap-2 px-1 text-base font-bold tracking-tight text-ink-soft">
        <span aria-hidden className="h-4 w-1 rounded-full bg-brand-600" />
        {title}
      </h2>
      {gridRows > 0 && (
        <div className={`grid flex-1 auto-rows-fr ${cols === 3 ? 'grid-cols-3' : 'grid-cols-2'} gap-2.5`}>
          {cells.map((m) => (
            <MenuCell key={m.key} item={m} />
          ))}
        </div>
      )}
      {rows.length > 0 && (
        <div className={`${SURFACE} divide-y divide-black/[0.06] overflow-hidden ${gridRows > 0 ? 'mt-2.5' : ''}`}>
          {rows.map((m) => (
            <MenuRow key={m.key} item={m} />
          ))}
        </div>
      )}
    </section>
  );
}

/** 카드 바탕 — 흰 판 · 옅은 윤곽. 칸(TILE)은 여기에 누르는 느낌을 더한다. */
const SURFACE = 'rounded-2xl bg-surface shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(15,23,42,0.08)] ring-1 ring-black/[0.04]';
const TILE = `${SURFACE} transition active:scale-[0.97] active:bg-canvas`;

/** 격자 한 칸 — 아이콘 위, 이름 아래(가운데). 높이는 묶음이 나눠 준 만큼 늘고 줄어든다. 밖으로 나가는 메뉴는 아이콘 귀퉁이에 작은 화살표. */
function MenuCell({ item }: { item: MenuItem }) {
  return (
    <button onClick={item.onClick} className={`${TILE} flex h-full min-h-[5.5rem] flex-col items-center justify-center gap-2 px-1.5 py-2.5 text-center`}>
      <span className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TINTS[item.tint]}`}>
        <Icon name={item.icon} className="h-6 w-6" />
        {item.external && (
          <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-surface text-ink-faint shadow ring-1 ring-black/5">
            <Icon name="external" className="h-3 w-3" />
          </span>
        )}
      </span>
      {/* 이름 자리는 늘 두 줄 높이 — 한 줄·두 줄 이름이 섞여도 아이콘이 같은 높이에 선다(10-09 way 「일정하게」). */}
      <span className="flex h-[2.5em] items-center justify-center break-keep text-[1.0625rem] font-semibold leading-tight text-ink">
        <span className="line-clamp-2">{item.busy ? '여는 중…' : item.label}</span>
      </span>
    </button>
  );
}

/** 목록 한 줄 — 카드 안에서 선으로 나뉜다. 아이콘 왼쪽 · 이름 · 오른쪽 화살표(밖으로 나가면 외부 표시). */
function MenuRow({ item }: { item: MenuItem }) {
  return (
    <button onClick={item.onClick} className="flex min-h-[3.5rem] w-full items-center gap-3 px-3.5 py-2 text-left transition active:bg-canvas">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TINTS[item.tint]}`}>
        <Icon name={item.icon} className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1 break-keep text-[1.0625rem] font-semibold leading-snug text-ink">{item.busy ? '여는 중…' : item.label}</span>
      <Icon name={item.external ? 'external' : 'chevronRight'} className="h-5 w-5 shrink-0 text-ink-faint" />
    </button>
  );
}
